/**
 * Vault auto-lock: the frontend half (plans/vault-lock-and-memory-hardening
 * §A2 + §A4). Two jobs, one lifecycle, started once from App.js:
 *
 * 1. ACTIVITY OUT — Rust owns the idle timer but sees no user interaction, so
 *    this reports it: capture-phase, passive listeners on `document` record
 *    into a local flag and flush `vault_touch_activity` at most once per 30
 *    seconds, and only while the store says the vault is unlocked. Rust also
 *    touches activity on every vault command, so a broken listener here
 *    degrades to "vault use keeps the vault open" rather than to a lock that
 *    never fires — which is why a failed flush is silently dropped.
 *
 * 2. LOCK IN — Rust emits `vault:locked` after clearing the key (auto-lock,
 *    lock-on-suspend, and after a manual `vault_lock` too). That event MUST
 *    run the same frontend cleanup as the manual button or auto-lock leaves
 *    the E2E private key and the in-flight unlock promise alive in JS; the
 *    shared body is `onVaultLocked` in vaultStore, subscribed here.
 *
 * No route-change hook: the app has no router, and switching tabs/panels is
 * itself a mousedown or keydown, so the four listeners already see it.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { useVaultStore, onVaultLocked } from '../store/vaultStore';

/**
 * Emitted by Rust for AUTONOMOUS locks only — the idle sweeper and the
 * suspend/screen-lock hooks. A manual `vault_lock` invoke does not echo it;
 * that path runs `onVaultLocked()` itself. (Getting this backwards is how the
 * manual path's direct call gets "cleaned up" and manual lock stops clearing
 * the E2E key cache.)
 */
export const VAULT_LOCKED_EVENT = 'vault:locked';

/**
 * 30s against a 60-minute default timeout is ample precision and caps the IPC
 * at 120 calls/hour (plan §A2).
 */
export const ACTIVITY_FLUSH_INTERVAL_MS = 30_000;

/**
 * "Any app interaction" (decision D1). Capture phase because focus and most
 * scrolls do not bubble; passive because none of these may ever delay input.
 */
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'focus'];

/**
 * Start reporting and listening. Returns stop(); App.js calls this from a
 * mount effect, so StrictMode's double-mount is start→stop→start, not two
 * instances.
 *
 * The seams exist for tests only — production callers pass nothing.
 */
export function startVaultActivity({
  flushIntervalMs = ACTIVITY_FLUSH_INTERVAL_MS,
  touch = () => invoke('vault_touch_activity'),
  isUnlocked = () => !!useVaultStore.getState().status?.unlocked,
  locked = onVaultLocked,
} = {}) {
  if (!isTauri() || typeof document === 'undefined') return () => {};

  let stopped = false;
  // -Infinity: the first interaction after start (or after a long idle) must
  // reach Rust immediately, not up to 30s later — a user who returns at
  // minute 59 and clicks must not be locked at minute 60 by flush latency.
  let lastFlushAt = -Infinity;
  // Activity seen inside the throttle window, not yet reported.
  let pending = false;
  let trailingTimer = null;

  const clearTrailing = () => {
    if (trailingTimer != null) {
      clearTimeout(trailingTimer);
      trailingTimer = null;
    }
  };

  const flush = () => {
    pending = false;
    lastFlushAt = Date.now();
    // Fire-and-forget by design — see the header: Rust's implicit per-command
    // touch is the degradation path, so a failed ping is noise, not an error.
    try {
      const p = touch();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      // ignore
    }
  };

  const onActivity = () => {
    if (stopped) return;
    // A locked vault has nothing to keep alive; flushing against it would be
    // pointless IPC and would spam a command that answers vault_locked.
    if (!isUnlocked()) return;
    const since = Date.now() - lastFlushAt;
    if (since >= flushIntervalMs) {
      flush();
      return;
    }
    // Inside the window: remember it and deliver when the window closes, so
    // no interaction is ever reported more than one interval late — and never
    // more than one flush per interval leaves this module.
    pending = true;
    if (trailingTimer == null) {
      trailingTimer = setTimeout(() => {
        trailingTimer = null;
        if (!stopped && pending && isUnlocked()) flush();
      }, Math.max(0, flushIntervalMs - since));
    }
  };

  for (const type of ACTIVITY_EVENTS) {
    document.addEventListener(type, onActivity, { capture: true, passive: true });
  }

  const lockedSub = subscribeTauriEvent(VAULT_LOCKED_EVENT, () => {
    if (stopped) return;
    // Anything still pending was activity against a vault that no longer
    // exists to keep open; reporting it after the lock would be noise.
    pending = false;
    clearTrailing();
    locked();
  });

  return function stop() {
    stopped = true;
    clearTrailing();
    for (const type of ACTIVITY_EVENTS) {
      document.removeEventListener(type, onActivity, { capture: true });
    }
    lockedSub.unlisten();
  };
}
